/* eslint-disable max-len */
import { useContext, useMemo } from 'react';
import { MainContext } from '../contexts';

const FALLBACK_LANG = 'ES';

// Global cache: since language is global, all components with same translations object
// can share the same computed result
const translationCache = new WeakMap<object, Map<string, Record<string, unknown>>>();

const getTranslationsForLang = (
  translations: Record<string, any>,
  lang: string
): Record<string, unknown> => {
  // Fast path: if the language is the same as fallback, return directly
  if (lang === FALLBACK_LANG) {
    return (translations[FALLBACK_LANG] || {}) as Record<string, unknown>;
  }

  // Only access translations once we know we need them
  const langTranslations = translations[lang] as Record<string, unknown>;
  
  // If the language does not exist, return the fallback translations
  if (!langTranslations) {
    return (translations[FALLBACK_LANG] || {}) as Record<string, unknown>;
  }

  const fallbackTranslations = translations[FALLBACK_LANG] as Record<string, unknown>;

  // If both have the same number of keys, assume complete translation
  // (optimization for cases where no fallback is needed)
  const langKeys = Object.keys(langTranslations);
  const fallbackKeys = Object.keys(fallbackTranslations || {});
  
  if (langKeys.length === fallbackKeys.length) {
    return langTranslations;
  }

  // Create object with fallback only when necessary
  // Use Object.create(null) for better performance (no prototype chain)
  const result: Record<string, unknown> = Object.create(null);
  
  // First copy all fallback translations
  if (fallbackTranslations) {
    for (const key in fallbackTranslations) {
      if (Object.prototype.hasOwnProperty.call(fallbackTranslations, key)) {
        result[key] = fallbackTranslations[key];
      }
    }
  }
  
  // Then override with language-specific translations
  for (const key in langTranslations) {
    if (Object.prototype.hasOwnProperty.call(langTranslations, key)) {
      result[key] = langTranslations[key];
    }
  }

  return result;
};

export const useTranslations = (translations = {}, defaultLang = 'ES'): Record<string, any> => {
  const { lang = defaultLang } = useContext(MainContext);

  return useMemo(() => {
    // Check global cache first
    let langCache = translationCache.get(translations);
    
    if (!langCache) {
      langCache = new Map();
      translationCache.set(translations, langCache);
    }

    // If we already computed this language, return cached result
    const cachedResult = langCache.get(lang);
    if (cachedResult) {
      return cachedResult;
    }

    // Compute and cache the result
    const result = getTranslationsForLang(translations, lang);
    langCache.set(lang, result);
    
    return result;
  }, [translations, lang]);
};
